// components/DeliveryReadinessPanel.js
// Fix: when relying on gap-night inference and no explicit Check-In row exists,
// prefer the last known clean on/before inStockDate (e.g., prior Check-Out Clean),
// so we do NOT claim "Cleaned today" unless we truly have same-day CI evidence.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import request from "../utils/request";
import { etaFromOrder } from "../utils/guestEta";
import { format } from "date-fns";

/* ---------- palette (hex only) ---------- */
const COLORS = {
  text: "#0F172A",
  subtext: "#64748B",
  card: "#FFFFFF",
  bg: "#F8FAFC",
  border: "#E2E8F0",
  chipBg: "#F1F5F9",
  chipText: "#334155",
  greenBg: "#ECFDF5",
  greenText: "#047857",
  redBg: "#FEF2F2",
  redText: "#B91C1C",
  amberBg: "#FFFBEB",
  amberText: "#92400E",
  blue: "#0A84FF",
  blueHover: "#0668CC",
  shadow: "0 16px 40px rgba(2,6,23,0.08)",
  subtleShadow: "0 8px 24px rgba(2,6,23,0.06)",
};

const DISPLAY_DELIVERY_TIME = "4:00 PM";

/* ---------- tiny utils ---------- */
const asText = (v) => (v == null ? "" : typeof v === "string" ? v : typeof v === "number" ? String(v) : "");
const notEmpty = (s) => typeof s === "string" && s.trim().length > 0;

/* Accepts "YYYY-MM-DD" or "MM/DD/YYYY" */
const parseD = (s) => {
  if (!s) return null;
  const str = String(s);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return new Date(str + "T00:00:00");
  const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return new Date(`${m[3]}-${m[1]}-${m[2]}T00:00:00`);
  const d = new Date(str);
  return isNaN(+d) ? null : d;
};
const fmtISO = (d) => (d instanceof Date && !isNaN(+d) ? d.toISOString().slice(0, 10) : "");
const ymd = (d) => (d instanceof Date && !isNaN(+d) ? format(d, "yyyy-MM-dd") : "");
const pp = (s) => {
  const d = parseD(s);
  return d ? format(d, "PP") : asText(s);
};
const atMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const daysBetween = (a, b) => {
  const A = parseD(a);
  const B = parseD(b);
  if (!A || !B) return null;
  return Math.round((atMidnight(B) - atMidnight(A)) / 86400000);
};
const sameDay = (a, b) => fmtISO(parseD(a) || new Date(a)) === fmtISO(parseD(b) || new Date(b));

/* ---------- grouping (when server returns flat cleans) ---------- */
function groupByReservation(rows) {
  const map = new Map();
  (rows || []).forEach((r) => {
    const key = String(r.confirmation_id || `${r.unit_id}:${r.start_date}:${r.end_date}`);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  });

  const out = [];
  for (const list of map.values()) {
    const any = list[0] || {};
    const start = asText(any.start_date);
    const end = asText(any.end_date);

    const checkinRow =
      list.find(
        (x) =>
          /check.?in/i.test(asText(x.cleaning_type)) &&
          (sameDay(x.cleaning_date, start) || sameDay(x.start_date, start))
      ) || null;

    const checkoutRow =
      list.find(
        (x) =>
          /check.?out/i.test(asText(x.cleaning_type)) &&
          (sameDay(x.cleaning_date, end) || sameDay(x.end_date, end))
      ) || null;

    out.push({
      start_date: start,
      end_date: end,
      rows: list,
      checkin: checkinRow
        ? {
            cleaning_type: asText(checkinRow.cleaning_type),
            event_status_name: asText(checkinRow.event_status_name),
            inspector_name: asText(checkinRow.inspector_name || ""),
            cleaning_date: asText(checkinRow.cleaning_date || start),
            time_text: DISPLAY_DELIVERY_TIME,
          }
        : null,
      checkout: checkoutRow
        ? {
            cleaning_type: asText(checkoutRow.cleaning_type),
            event_status_name: asText(checkoutRow.event_status_name),
            cleaning_date: asText(checkoutRow.cleaning_date || end),
            time_text: DISPLAY_DELIVERY_TIME,
          }
        : null,
    });
  }

  out.sort((a, b) => (parseD(a.start_date) || 0) - (parseD(b.start_date) || 0));
  return out;
}

function findCurrentReservation(reservations, inStockKey) {
  return (reservations || []).find((r) => r.start_date === inStockKey) || null;
}
function findPreviousReservation(reservations, inStockKey) {
  const prevs = (reservations || []).filter((r) => r.end_date && r.end_date < inStockKey);
  prevs.sort((a, b) => (parseD(b.end_date) || 0) - (parseD(a.end_date) || 0));
  return prevs[0] || null;
}

/* ---------- gap vs back-to-back ---------- */
const isBackToBack = (night_gap) => night_gap === 0;
const hasGapNight = (night_gap) => typeof night_gap === "number" && night_gap >= 1;

const chooseCheckInRow = (rows = []) => rows.find((r) => /check.?in/i.test(asText(r.cleaning_type)));

/* ---------- check-in signal ladder ---------- */
function inferCleanedOn(reservation) {
  if (!reservation) return { cleaned_on: null, level: "no_reservation", inspector: null };
  const ci =
    reservation.checkin ||
    chooseCheckInRow(reservation.rows || []) || {};
  const status = asText(ci.event_status_name);
  const ciDate = parseD(ci.cleaning_date);
  const hasInspector = notEmpty(ci.inspector_name);

  if (/clean w\/ inspection/i.test(status) && ciDate) {
    return { cleaned_on: ciDate, level: "inspected_complete", inspector: asText(ci.inspector_name || "") };
  }
  if (hasInspector && ciDate) {
    return { cleaned_on: ciDate, level: "inspector_witness", inspector: asText(ci.inspector_name || "") };
  }
  if (ciDate) {
    return { cleaned_on: ciDate, level: "scheduled_on_arrival", inspector: asText(ci.inspector_name || "") };
  }
  // fallback: assume cleaned on arrival date if no explicit CI row
  return { cleaned_on: parseD(reservation.start_date), level: "assumed_by_checkin", inspector: null };
}

/* ---------- collect all clean rows for evidence ---------- */
function collectAllRows(payload) {
  const set = new Set();
  const out = [];

  const pushRows = (rows) => {
    (rows || []).forEach((r) => {
      const key =
        `${asText(r.cleaning_id)}|${asText(r.cleaning_type)}|${asText(r.cleaning_date)}|${asText(r.start_date)}|${asText(r.end_date)}`;
      if (!set.has(key)) {
        set.add(key);
        out.push(r);
      }
    });
  };

  if (Array.isArray(payload?.cleans)) pushRows(payload.cleans);
  if (Array.isArray(payload?.reservations)) {
    payload.reservations.forEach((res) => pushRows(res.rows));
  }
  if (payload?.previousReservation) pushRows(payload.previousReservation.rows);
  if (Array.isArray(payload?.reservationsAround)) {
    payload.reservationsAround.forEach((res) => pushRows(res.rows));
  }
  return out;
}

function lastKnownCleanOnOrBefore(allRows, inStockKey) {
  const D = parseD(inStockKey);
  if (!D) return null;
  let best = null;
  (allRows || []).forEach((r) => {
    const dt = parseD(r?.cleaning_date);
    if (!dt) return;
    // any cleaning record (check-in or check-out) counts as the last execution of a clean task
    if (atMidnight(dt) <= atMidnight(D)) {
      if (!best || atMidnight(dt) > atMidnight(best)) best = dt;
    }
  });
  return best;
}

/* ---------- pick upcoming arrival ---------- */
function findUpcomingArrival(payload, inStockKey, groupedReservations) {
  const D = parseD(inStockKey);
  if (!D) return null;
  const same = (s) => !!s && parseD(s) && parseD(s).getTime() === D.getTime();

  if (payload?.currentReservation?.start_date && same(payload.currentReservation.start_date)) {
    return payload.currentReservation;
  }
  const r = (payload?.reservations || []).find((x) => same(x.start_date));
  if (r) return r;
  const g = (groupedReservations || []).find((x) => same(x.start_date));
  if (g) return g;
  const p = payload?.previousReservation;
  if (p && same(p.next_arrival_date)) {
    return {
      start_date: inStockKey,
      rows: [],
      prev_departure_date: p.end_date,
      night_gap: p.night_gap,
      next_arrival_time: DISPLAY_DELIVERY_TIME,
      checkin: null,
    };
  }
  return null;
}

/* ---------- delivery verdict for inStockDate ---------- */
function cleanStatusOnInStockDate(payload, inStockKey, arrival, previousReservation, allRows) {
  const D = parseD(inStockKey);
  if (!D || !arrival) {
    return {
      clean_at_day_start: false,
      clean_calendar_day: false,
      inference_level: "no_reservation",
      has_gap_night: false,
      back_to_back: false,
      cleaned_on: null,
      rationale: "no_arrival_found_for_inStockDate",
      inspector: null,
      same_day_checkout: false,
    };
  }

  const gap =
    typeof arrival.night_gap === "number"
      ? arrival.night_gap
      : typeof previousReservation?.night_gap === "number"
      ? previousReservation.night_gap
      : null;

  const gapNight = hasGapNight(gap);
  const b2b = isBackToBack(gap);

  const { cleaned_on: ciCleanedOn, level, inspector } = inferCleanedOn(arrival);

  // New: compute last known clean on/before D from all rows (incl. prior checkout)
  const lastKnown = lastKnownCleanOnOrBefore(allRows, inStockKey);

  // Choose displayed cleaned_on:
  // - Prefer explicit CI date if it exists and is <= D.
  // - If relying on gap-night and CI is assumed/same-day, prefer lastKnown.
  let displayCleanedOn = ciCleanedOn && atMidnight(ciCleanedOn) <= atMidnight(D) ? ciCleanedOn : null;

  if (gapNight) {
    if (!displayCleanedOn || fmtISO(displayCleanedOn) === fmtISO(D) || level === "assumed_by_checkin") {
      if (lastKnown) displayCleanedOn = lastKnown; // use 08/19 in your example
    }
  }

  // Final booleans
  let clean_at_day_start = !!displayCleanedOn && atMidnight(displayCleanedOn) < atMidnight(D);
  const clean_sometime_today = !!displayCleanedOn && atMidnight(displayCleanedOn) <= atMidnight(D);

  let clean_calendar_day = clean_sometime_today;
  let rationale = level;

  if (b2b) {
    const strong = level === "inspected_complete" || level === "inspector_witness";
    clean_calendar_day = strong && clean_sometime_today;
    if (!clean_calendar_day) rationale = "back_to_back_without_strong_signal";
  } else if (gapNight) {
    // Treat as delivery-safe for the date regardless of explicit CI row
    clean_calendar_day = true;

    // If we still have no displayCleanedOn (data gaps), assume day-start safe for dispatcher simplicity
    if (!displayCleanedOn) clean_at_day_start = true;

    if (level === "scheduled_on_arrival" && fmtISO(ciCleanedOn) === fmtISO(D)) {
      rationale = "gap_night_but_clean_scheduled_today";
    } else {
      rationale = "gap_night";
    }
  }

  const same_day_checkout =
    !!previousReservation?.end_date && sameDay(previousReservation.end_date, inStockKey);

  return {
    clean_at_day_start,
    clean_calendar_day,
    inference_level: level,
    has_gap_night: gapNight,
    back_to_back: b2b,
    cleaned_on: displayCleanedOn ? fmtISO(displayCleanedOn) : null,
    rationale,
    inspector,
    next_arrival_time: DISPLAY_DELIVERY_TIME,
    same_day_checkout,
  };
}

/* ---------- UI atoms ---------- */
function Chip({ label, title, tone = "neutral" }) {
  if (!asText(label)) return null;
  const palette =
    tone === "success"
      ? { bg: COLORS.greenBg, text: COLORS.greenText }
      : tone === "danger"
      ? { bg: COLORS.redBg, text: COLORS.redText }
      : tone === "warn"
      ? { bg: COLORS.amberBg, text: COLORS.amberText }
      : { bg: COLORS.chipBg, text: COLORS.chipText };
  return (
    <span
      className="px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap max-w-[180px] truncate"
      title={title || asText(label)}
      style={{ backgroundColor: palette.bg, color: palette.text }}
    >
      {asText(label)}
    </span>
  );
}
function Pill({ icon: Icon, children, title }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap max-w-[240px] truncate"
      title={title || (typeof children === "string" ? children : undefined)}
      style={{ backgroundColor: COLORS.chipBg, color: COLORS.chipText }}
    >
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" /> : null}
      {children}
    </span>
  );
}

/* ---------- main ---------- */
export default function DeliveryReadinessPanel({ order }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const delivery = useMemo(
    () => (order?.inStockDate ? parseD(order.inStockDate) || new Date() : new Date()),
    [order?.inStockDate]
  );
  const inStockKey = useMemo(() => ymd(delivery), [delivery]);
const ETA = useMemo(() => etaFromOrder(order), [order?.property?.checkInTime, order?.inStockDate]);
  const fetchReport = useCallback(async () => {
    if (!order?._id) return;
    setLoading(true);
    setErr(null);

    const initialForwardDays = 7;
    const tryFetch = async (forwardDays) =>
      request.get(`/orders/${order._id}/cleaning-report`, {
        params: { t: Date.now(), backDays: 7, forwardDays },
        validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
        headers: { "Cache-Control": "no-cache" },
      });

    try {
      let res = await tryFetch(initialForwardDays);
      let payload = res.data || null;

      const rows = payload?.cleans || [];
      const grouped = groupByReservation(rows);
      const arrival = findUpcomingArrival(payload, inStockKey, grouped);

      if (!arrival) {
        res = await tryFetch(Math.max(initialForwardDays, 10));
        payload = res.data || payload;
      }

      setData(payload);
      setLastLoadedAt(new Date());
    } catch (e) {
      setErr(asText(e?.response?.data?.error) || "Could not load cleaning report.");
    } finally {
      setLoading(false);
    }
  }, [order?._id, inStockKey]);

  useEffect(() => {
    fetchReport();
  }, [order?._id]);

  /* ---------- derive context ---------- */
  const rows = data?.cleans || [];
  const reservationsGrouped = useMemo(() => groupByReservation(rows), [rows]);

  const currentFromServer = data?.currentReservation || null;
  const previousFromServer = data?.previousReservation || null;

  const currentRes = useMemo(() => {
    if (currentFromServer?.start_date === inStockKey) return currentFromServer;
    return findCurrentReservation(reservationsGrouped, inStockKey);
  }, [currentFromServer, reservationsGrouped, inStockKey]);

  const previousRes = useMemo(() => {
    if (previousFromServer?.end_date && previousFromServer.end_date < inStockKey) return previousFromServer;
    return findPreviousReservation(reservationsGrouped, inStockKey);
  }, [previousFromServer, reservationsGrouped, inStockKey]);

  const arrival = useMemo(
    () => findUpcomingArrival(data, inStockKey, reservationsGrouped),
    [data, inStockKey, reservationsGrouped]
  );

  // Collect all rows one time for last-known-clean logic
  const allRows = useMemo(() => collectAllRows(data || {}), [data]);

  const verdict = useMemo(
    () => cleanStatusOnInStockDate(data, inStockKey, arrival, previousRes, allRows),
    [data, inStockKey, arrival, previousRes, allRows]
  );

  /* ---------- dispatcher chips ---------- */
  const isCleanAllDay = verdict.clean_at_day_start && verdict.clean_calendar_day;
  const isCleanToday = verdict.clean_calendar_day;

  const primaryChip = isCleanAllDay
    ? { label: "Cleaned", tone: "success" }
    : isCleanToday
    ? { label: "Cleaning Today", tone: "warn" }
    : { label: "Not Cleaned", tone: "danger" };

  const secondaryChip = isCleanAllDay
    ? { label: "Deliver Any Time", tone: "success" }
    : isCleanToday
    ? { label: `Deliver After ${DISPLAY_DELIVERY_TIME}`, tone: "warn" }
    : { label: "Hold Delivery", tone: "danger" };

  const sameDayCheckout = verdict.same_day_checkout;

  const lastCheckoutDays = previousRes?.end_date ? daysBetween(previousRes.end_date, inStockKey) : null;
  const lastCheckoutText =
    lastCheckoutDays == null
      ? ""
      : lastCheckoutDays === 0
      ? "Last Checkout: today"
      : lastCheckoutDays === 1
      ? "Last Checkout: 1 day ago"
      : `Last Checkout: ${lastCheckoutDays} days ago`;

  /* ---------- banner reasoning ---------- */
  const BannerIcon = isCleanToday ? CheckCircleIcon : verdict.back_to_back ? NoSymbolIcon : ExclamationTriangleIcon;
  const bannerStyle = isCleanToday
    ? { bg: COLORS.greenBg, text: COLORS.greenText }
    : verdict.back_to_back
    ? { bg: COLORS.redBg, text: COLORS.redText }
    : { bg: COLORS.amberBg, text: COLORS.amberText };

  const headline = isCleanAllDay
    ? "Ready all day"
    : isCleanToday
    ? "Ready today after cleaning"
    : "Not ready for delivery";

  const headReason = (() => {
    if (!arrival) return "No arrival found for this delivery date";
    if (isCleanAllDay) {
      if (verdict.inference_level === "inspected_complete") return "Inspected complete";
      if (verdict.inference_level === "inspector_witness") return "Inspector verified";
      if (verdict.rationale === "gap_night") return lastCheckoutText;
      return "Cleaned before day start";
    }
    if (isCleanToday) {
      if (sameDayCheckout) return "Checkout today. Deliver after cleaning is complete";
      if (verdict.rationale === "gap_night_but_clean_scheduled_today") return "Slack exists. Cleaning scheduled today";
      return "Cleaning expected today";
    }
    if (verdict.rationale === "back_to_back_without_strong_signal") return "Tight turn and no proof of completion";
    return "Insufficient evidence of clean";
  })();

  /* ---------- rationale→evidence mapping ---------- */
  const evidenceCopyFromRationale = (rationale, level) => {
    switch (rationale) {
      case "gap_night":
        return "Gap night before arrival";
      case "gap_night_but_clean_scheduled_today":
        return "Gap night; cleaning scheduled today";
      case "back_to_back_without_strong_signal":
        return "Back-to-back; awaiting strong signal";
      default:
        if (level === "inspected_complete") return "Marked clean with inspection";
        if (level === "inspector_witness") return "Inspector verified";
        if (level === "scheduled_on_arrival") return "Cleaning scheduled on arrival day";
        if (level === "assumed_by_checkin") return "Assumed due to arrival";
        return "No evidence";
    }
  };

  return (
    <section
      className="w-full overflow-hidden"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 20, boxShadow: COLORS.shadow }}
    >
<div className="min-w-0 w-full px-2 text-center">
  <h1
    className="text-[20px] sm:text-[22px] font-semibold leading-tight tracking-tight truncate"
    style={{ color: COLORS.text }}
    title="Cleaning Report"
  >
    Cleaning Report
  </h1>
  <div
    className="mt-1 text-xs sm:text-[13px] leading-5 whitespace-nowrap overflow-hidden text-ellipsis"
    style={{ color: COLORS.subtext }}
  >
    <span>Delivery date: {format(parseD(inStockKey), "PP")}</span>
    <span className="ml-1 text-[11px] opacity-80">({inStockKey})</span>
  </div>
</div>

      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
        
        <div className="flex items-center gap-3 min-w-0">
          <CalendarDaysIcon className="h-6 w-6 shrink-0" style={{ color: COLORS.subtext }} />

          <div className="flex flex-wrap items-center gap-2 ml-3">
            {lastCheckoutText ? <Pill title={lastCheckoutText}>{lastCheckoutText}</Pill> : null}
            {sameDayCheckout ? <Pill icon={ExclamationTriangleIcon}>Checkout Today</Pill> : null}
          </div>
        </div>

        <button
          type="button"
          onClick={fetchReport}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-colors"
          style={{ backgroundColor: COLORS.blue, color: "#FFFFFF", boxShadow: COLORS.subtleShadow }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = COLORS.blueHover)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = COLORS.blue)}
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Meta */}
      <div className="px-5 pt-3 pb-2 space-y-1">
        {data?.dateRange?.start && data?.dateRange?.end ? (
          <div className="text-xs truncate" style={{ color: COLORS.subtext }}>
            Query window: {pp(data.dateRange.start)} to {pp(data.dateRange.end)}
          </div>
        ) : null}
        {lastLoadedAt ? (
          <div className="text-xs truncate" style={{ color: COLORS.subtext }}>
            Last updated: {format(lastLoadedAt, "PPp")}
          </div>
        ) : null}
      </div>

      {/* Error */}
      {err ? (
        <div
          role="alert"
          className="mx-5 mb-4 rounded-xl px-3.5 py-3 flex items-start gap-2"
          style={{ backgroundColor: COLORS.redBg, color: COLORS.redText, border: `1px solid ${COLORS.border}` }}
        >
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <p className="text-sm break-words">{err}</p>
        </div>
      ) : null}

      {/* Verdict */}
      {!err ? (
        <div
          className="mx-5 mb-4 rounded-2xl px-4 py-3.5 flex flex-wrap items-start gap-3"
          style={{ backgroundColor: bannerStyle.bg, color: bannerStyle.text, border: `1px solid ${COLORS.border}` }}
        >
          <BannerIcon className="h-5 w-5 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{headline}</div>
            <div className="text-xs break-words">{headReason}</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Chip label={primaryChip.label} tone={primaryChip.tone} />
              <Chip label={secondaryChip.label} tone={secondaryChip.tone} />
              {verdict.cleaned_on ? <Pill icon={ClockIcon}>Cleaned {pp(verdict.cleaned_on)}</Pill> : null}
              {verdict.inspector ? <Pill icon={ShieldCheckIcon}>Inspector {verdict.inspector}</Pill> : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* Details */}
      {!err ? (
        <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* Arrival Context */}
          <Card label="Arrival Context">
            {arrival ? (
              <>
                <Line label="Guest Arrival Date" value={pp(arrival.start_date)} />
                <Line label="Last Checkout Date" value={pp(arrival.prev_departure_date || previousRes?.end_date || "")} />
                <Line label="Guest ETA" value={ETA} />
                {sameDayCheckout ? (
                  <Line
                    label="Note"
                    value="Checkout and delivery date match. Send after cleaning completes at 4:00 PM."
                  />
                ) : null}
              </>
            ) : (
              <div className="text-sm font-medium" style={{ color: COLORS.text }}>
                No arrival on this date
              </div>
            )}
          </Card>
          {/* Previous Checkout (concise) */}
          <Card label="Previous Checkout">
            {previousRes ? (
              <>
                <Line label="Checkout date" value={pp(previousRes.end_date)} />
                <Line label="CO clean date" value={pp(previousRes.checkout?.cleaning_date || previousRes.end_date)} />
                {lastCheckoutText ? <Line label="Summary" value={lastCheckoutText} /> : null}
              </>
            ) : (
              <div className="text-sm font-medium" style={{ color: COLORS.text }}>
                Not found
              </div>
            )}
          </Card>
          {/* Evidence (driven by rationale + last-known-clean) */}
          <Card label="Check-In Clean Evidence">
            {arrival ? (
              (() => {
                const on = verdict.cleaned_on ? pp(verdict.cleaned_on) : "Unknown";
                const evidence = evidenceCopyFromRationale(verdict.rationale, verdict.inference_level);
                return (
                  <>
                    <Line label="Cleaned on" value={on} />
                    <Line label="Evidence" value={evidence} />
                    <Line label="Inspector" value={verdict.inspector || "Unassigned"} />
                  </>
                );
              })()
            ) : (
              <div className="text-sm font-medium" style={{ color: COLORS.text }}>
                No evidence available
              </div>
            )}
          </Card>


        </div>
      ) : null}

      {/* Warnings / partial */}
      {!err && (data?.warnings?.length || data?.partial) ? (
        <div className="px-5 pb-5">
          <div
            className="rounded-xl px-3.5 py-3 text-xs flex items-start gap-2"
            style={{ backgroundColor: COLORS.amberBg, color: COLORS.amberText, border: `1px solid ${COLORS.border}` }}
          >
            <InformationCircleIcon className="h-4 w-4 mt-0.5 shrink-0" />
            <span className="break-words">
              {data?.partial ? "Partial data. " : ""}
              {(data?.warnings || []).join(" ")}
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/* ---------- tiny subcomponents ---------- */
function Card({ label, emptyLabel = "None", children }) {
  const isEmpty = !children || (Array.isArray(children) && children.filter(Boolean).length === 0);
  return (
    <div
      className="rounded-2xl h-full overflow-hidden"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, boxShadow: COLORS.shadow }}
    >
      <div className="px-4 py-3 border-b" style={{ borderColor: COLORS.border }}>
        <div className="text-[11px] font-semibold uppercase tracking-wide truncate" style={{ color: COLORS.subtext }}>
          {label}
        </div>
      </div>
      <div className="p-4 sm:p-5 text-sm space-y-2">
        {isEmpty ? (
          <div className="font-semibold" style={{ color: COLORS.text }}>
            {emptyLabel}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
function Line({ label, value }) {
  return (
    <div className="grid grid-cols-[140px,1fr] gap-x-3 items-baseline">
      <div className="text-xs truncate" style={{ color: COLORS.subtext }}>
        {label}
      </div>
      <div className="text-sm font-medium min-w-0 break-words" style={{ color: COLORS.text }}>
        {value}
      </div>
    </div>
  );
}
